import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Spinner } from "reactstrap";
import DateInput from "../../../components/Common/DateInput";
import { useProfileUser } from "../../../feautures/settings/hooks/useProfileUser";
import BackButton from "../../../components/Common/BackButton";

/* ============================================================================
 * POS Rapor hiyerarşisinin ortak parçaları.
 *
 * Hiyerarşi:
 *   POS Rapor → POS Sağlayıcı → Firmalar → Firma → (İşlem Detayı | Cariler)
 *                                                       Cariler → Cari → İşlem Detayı
 *
 * Renk/kart stilleri mevcut POS ekranlarıyla (POSAnalysisDashboard) birebir aynıdır.
 * ========================================================================== */

export const NAVY = "#0A316E";
export const ORANGE = "#E8833A";
export const WARN = "#dc3545";

export const POS_REPORT_ROOT = "/admin/pos-raporu";

/** Sağlayıcı enum adı → görünen ad. Backend providerLabel döndüğünde o tercih edilir. */
export const PROVIDER_LABELS = {
    PAYTR: "PayTR",
    PARAMPOS: "ParamPOS",
    IYZICO: "iyzico",
    ESNEK_POS: "Hoppa",
    QNB_PAYFOR: "QNB PayFor",
    GARANTI: "Garanti BBVA",
};

export const providerLabelOf = (key) => PROVIDER_LABELS[key] || key || "Bilinmiyor";

export const trNum = (n) => new Intl.NumberFormat("tr-TR").format(Math.round(Number(n ?? 0)));
export const fmtTL = (n) => (n == null ? "—" : "₺" + trNum(n));
export const fmtMoney2 = (n) =>
    n == null
        ? "—"
        : "₺" +
          new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
              .format(Number(n));
export const fmtPct = (n) => (n == null ? "—" : "%" + Number(n).toFixed(2));
export const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("tr-TR") : "—");
export const fmtDateTime = (d) =>
    d ? new Date(d).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" }) : "—";

export const fmtCompactTL = (n) => {
    const v = Number(n ?? 0);
    if (Math.abs(v) >= 1_000_000) return "₺" + (v / 1_000_000).toFixed(2) + "M";
    if (Math.abs(v) >= 1_000) return "₺" + (v / 1_000).toFixed(0) + "K";
    return "₺" + trNum(v);
};

/* ------------------------------- dönem aralığı ------------------------------ */

// Yerel tarih bileşenlerinden ISO (toISOString UTC'ye çevirip TR'de günü kaydırır).
const iso = (dt) => {
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const d = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
};

const firstOfMonth = () => {
    const d = new Date();
    return iso(new Date(d.getFullYear(), d.getMonth(), 1));
};
const todayIso = () => iso(new Date());

/**
 * Hiyerarşi ekranlarının tarih aralığı filtresi. POS sağlayıcı zaten route'tan
 * geldiği için (tasarım notu: "pos sağlayıcı filtresi kalkabilir, sadece tarih
 * filtreleme yeterli") bu ekranlarda tek filtre tarih aralığıdır.
 * Aralık query string'de taşınır → hiyerarşide gezinirken korunur, link paylaşılabilir.
 */
export const usePosDateRange = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const startDate = searchParams.get("start") || firstOfMonth();
    const endDate = searchParams.get("end") || todayIso();
    const apply = (nextStart, nextEnd) => {
        const p = new URLSearchParams(searchParams);
        p.set("start", nextStart);
        p.set("end", nextEnd);
        setSearchParams(p, { replace: true });
    };
    // Alt ekranlara taşınacak query (tab bilgisi hariç)
    const rangeQuery = `?start=${startDate}&end=${endDate}`;
    return { startDate, endDate, apply, rangeQuery };
};

/* --------------------------------- filtreler -------------------------------- */

// Boş değer filtreyi kaldırır; böylece "kaç filtre aktif" sayımı da doğru olur.
const withValue = (values, key, value) => {
    const next = { ...values };
    if (value === "" || value == null) delete next[key];
    else next[key] = value;
    return next;
};

/**
 * Filtre değerleri iki kopya halinde tutulur: {@code draft} yazarken güncellenir,
 * {@code applied} ise tabloyu besleyendir. "Filtrele" düğmesi yoktur ama her
 * harfte de sorgu atılmaz: metin/sayı kutuları yazma bitince (kutudan çıkınca
 * veya Enter'a basınca) uygulanır. Seçim kutuları ve tarihler tek hamlede
 * tamamlandığı için anında uygulanır.
 */
export const usePosFilters = () => {
    const [draft, setDraft] = React.useState({});
    const [applied, setApplied] = React.useState({});

    const set = React.useCallback((key, value, immediate = false) => {
        const next = withValue(draft, key, value);
        setDraft(next);
        if (immediate) setApplied(next);
    }, [draft]);

    /** Yazılan değeri tabloya yansıtır (blur / Enter). */
    const commit = React.useCallback(() => setApplied(draft), [draft]);

    const reset = React.useCallback(() => {
        setDraft({});
        setApplied({});
    }, []);

    return { draft, applied, set, commit, reset, activeCount: Object.keys(draft).length };
};

// Serbest metin araması bu alanlarda yapılır (backend'deki PosTableFilter ile aynı mantık).
const SEARCH_FIELDS = [
    "companyName", "accountName", "accountCode", "providerLabel",
    "merchantOid", "cardBrand",
];

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Yerel filtreleme — tamamı elde olan tablolar için (kök tablo, işlem dökümü).
 * Anahtar sözleşmesi backend ile aynıdır: {@code search} serbest metin,
 * {@code min<Alan>}/{@code max<Alan>} aralık, diğer anahtarlar eşitlik.
 * {@code matchers} ile bir anahtara özel kural verilebilir.
 */
export const filterPosRows = (rows, values, matchers = {}) => {
    const entries = Object.entries(values || {}).filter(([, v]) => v !== "" && v != null);
    if (!rows?.length || !entries.length) return rows;
    return rows.filter((row) => entries.every(([key, value]) => (
        matchers[key] ? matchers[key](row, value) : matchOne(row, key, value)
    )));
};

const matchOne = (row, key, value) => {
    if (key === "search") {
        const needle = String(value).trim().toLowerCase();
        return SEARCH_FIELDS.some((f) => String(row[f] ?? "").toLowerCase().includes(needle));
    }
    const bound = /^(min|max)([A-Z].*)$/.exec(key);
    if (bound) {
        const [, kind, rest] = bound;
        const field = rest.charAt(0).toLowerCase() + rest.slice(1);
        const isDate = DATE_SORT_KEYS.has(field);
        let limit = isDate ? Date.parse(value) : Number(value);
        if (!Number.isFinite(limit)) return true;          // yarım girilmiş değer filtrelemez
        // Gün bazlı üst sınır o günün tamamını kapsamalı (saat taşıyan alanlar için).
        if (isDate && kind === "max") limit += DAY_MS - 1;
        const actual = isDate ? Date.parse(row[field]) : Number(row[field] ?? 0);
        if (!Number.isFinite(actual)) return false;
        return kind === "min" ? actual >= limit : actual <= limit;
    }
    return String(row[key] ?? "") === String(value);
};

/**
 * Tarih aralığı + "Gelişmiş Filtreleme" çubuğu.
 *
 * Filtrele düğmesi yoktur. Tarih ve seçim kutuları seçildiği anda uygulanır;
 * metin/sayı kutuları ise yazma bitince (kutudan çıkınca veya Enter'a basınca)
 * uygulanır — her harfte sorgu atılmaz. {@code fields} boş verilirse yalnız
 * tarih aralığı gösterilir.
 *
 * fields: [{ key, label, type: "text"|"number"|"select"|"range", options,
 *            placeholder, minKey, maxKey, inputType }]
 */
export const PosFilterBar = ({
    startDate, endDate, onRangeChange,
    fields = [], values = {}, onChange, onCommit, onReset, activeCount = 0,
    right,
}) => {
    const [open, setOpen] = React.useState(false);
    const [s, setS] = React.useState(startDate);
    const [e, setE] = React.useState(endDate);

    React.useEffect(() => { setS(startDate); setE(endDate); }, [startDate, endDate]);

    // Tarih kutusu ya tam bir tarih verir ya da boş kalır; ara değer üretmediği
    // için seçim anında uygulanır.
    const applyRange = (nextStart, nextEnd) => {
        setS(nextStart);
        setE(nextEnd);
        if (nextStart && nextEnd && (nextStart !== startDate || nextEnd !== endDate)) {
            onRangeChange(nextStart, nextEnd);
        }
    };

    const hasFields = fields.length > 0;

    return (
        <div className="bg-white p-3 mb-3" style={{ borderRadius: 16, border: "1px solid #eef0f4" }}>
            <div className="row g-2 align-items-end">
                <div className="col-md-3">
                    <label className="form-label small text-muted mb-1">Başlangıç</label>
                    <DateInput value={s} onChange={(iso) => applyRange(iso, e)} />
                </div>
                <div className="col-md-3">
                    <label className="form-label small text-muted mb-1">Bitiş</label>
                    <DateInput value={e} onChange={(iso) => applyRange(s, iso)} />
                </div>
                <div className="col-md-3">
                    {hasFields && (
                        <AdvancedFilterToggle
                            className="w-100"
                            open={open}
                            onToggle={() => setOpen((v) => !v)}
                            activeCount={activeCount}
                        />
                    )}
                </div>
                <div className="col-md-3 d-flex justify-content-end">{right}</div>
            </div>

            {hasFields && open && (
                <AdvancedFilterPanel
                    fields={fields}
                    values={values}
                    onChange={onChange}
                    onCommit={onCommit}
                    onReset={onReset}
                    activeCount={activeCount}
                />
            )}
        </div>
    );
};

/**
 * Kendi tarih alanı olan ekranlar (POS Rapor kökü) için yalnız gelişmiş filtre
 * bölümü: düğme + altında açılan panel.
 */
export const PosAdvancedFilters = ({ fields = [], values = {}, onChange, onCommit, onReset, activeCount = 0 }) => {
    const [open, setOpen] = React.useState(false);
    if (!fields.length) return null;
    return (
        <div className="mb-3">
            <AdvancedFilterToggle
                open={open}
                onToggle={() => setOpen((v) => !v)}
                activeCount={activeCount}
                pill
            />
            {open && (
                <div className="bg-white p-3 mt-2" style={{ borderRadius: 16, border: "1px solid #eef0f4" }}>
                    <AdvancedFilterPanel
                        fields={fields}
                        values={values}
                        onChange={onChange}
                        onCommit={onCommit}
                        onReset={onReset}
                        activeCount={activeCount}
                        flush
                    />
                </div>
            )}
        </div>
    );
};

const AdvancedFilterToggle = ({ open, onToggle, activeCount, className = "", pill }) => (
    <button
        type="button"
        className={`btn d-flex align-items-center justify-content-center gap-1 ${className}`}
        style={{
            borderRadius: pill ? 999 : 10,
            padding: pill ? ".35rem 1rem" : undefined,
            fontSize: pill ? ".8rem" : undefined,
            border: `1px solid ${open || activeCount ? ORANGE : "#dfe3ea"}`,
            background: open || activeCount ? ORANGE : "#fff",
            color: open || activeCount ? "#fff" : "#5c6a86",
            fontWeight: 600,
        }}
        onClick={onToggle}
    >
        <i className="bx bx-slider-alt" />
        Gelişmiş Filtreleme
        {activeCount > 0 && (
            <span
                className="d-inline-flex align-items-center justify-content-center"
                style={{
                    background: "#fff", color: ORANGE, borderRadius: 999,
                    minWidth: 20, height: 20, fontSize: ".72rem", fontWeight: 700,
                }}
            >
                {activeCount}
            </span>
        )}
        <i className={`bx ${open ? "bx-chevron-up" : "bx-chevron-down"}`} />
    </button>
);

const AdvancedFilterPanel = ({ fields, values, onChange, onCommit, onReset, activeCount, flush }) => (
    <div className={flush ? "" : "mt-3 pt-3"} style={flush ? undefined : { borderTop: "1px dashed #e7eaf0" }}>
        <div className="row g-2 align-items-end">
            {fields.map((f) => (
                <FilterField key={f.key || f.minKey} field={f} values={values}
                             onChange={onChange} onCommit={onCommit} />
            ))}
        </div>
        {/* Temizlenecek bir şey yokken düğme hiç gösterilmez. */}
        {activeCount > 0 && (
            <div className="d-flex justify-content-end mt-2">
                <button
                    type="button"
                    className="btn btn-sm btn-link text-decoration-none"
                    style={{ color: "#8a93a6" }}
                    onClick={onReset}
                >
                    <i className="bx bx-x me-1" />Filtreleri temizle
                </button>
            </div>
        )}
    </div>
);

/**
 * Yazılan değer kutudan çıkınca (blur) ya da Enter'a basınca uygulanır — her
 * harfte tablo yenilenmez. Tarih kutuları ara değer üretmediği için anında
 * uygulanır.
 */
const CommitInput = ({ type, className, placeholder, value, onChange, onCommit }) => {
    const immediate = type === "date";
    return (
        <input
            type={type}
            className={className}
            placeholder={placeholder}
            value={value}
            onChange={(ev) => onChange(ev.target.value, immediate)}
            onBlur={() => { if (!immediate) onCommit?.(); }}
            onKeyDown={(ev) => {
                // Enter/Escape odaktan çıkarır, uygulama blur'da olur.
                if (!immediate && (ev.key === "Enter" || ev.key === "Escape")) ev.currentTarget.blur();
            }}
        />
    );
};

const FilterField = ({ field, values, onChange, onCommit }) => {
    const cls = field.width || "col-md-3";
    if (field.type === "range") {
        const inputType = field.inputType || "number";
        return (
            <div className={cls}>
                <label className="form-label small text-muted mb-1">{field.label}</label>
                <div className="d-flex gap-1">
                    <CommitInput
                        type={inputType}
                        className="form-control"
                        placeholder={inputType === "date" ? undefined : "En az"}
                        value={values[field.minKey] ?? ""}
                        onChange={(v, now) => onChange(field.minKey, v, now)}
                        onCommit={onCommit}
                    />
                    <CommitInput
                        type={inputType}
                        className="form-control"
                        placeholder={inputType === "date" ? undefined : "En çok"}
                        value={values[field.maxKey] ?? ""}
                        onChange={(v, now) => onChange(field.maxKey, v, now)}
                        onCommit={onCommit}
                    />
                </div>
            </div>
        );
    }
    if (field.type === "select") {
        return (
            <div className={cls}>
                <label className="form-label small text-muted mb-1">{field.label}</label>
                <select
                    className="form-select"
                    value={values[field.key] ?? ""}
                    // Seçim tek hamlede tamamlanır; blur beklemeye gerek yok.
                    onChange={(ev) => onChange(field.key, ev.target.value, true)}
                >
                    <option value="">Tümü</option>
                    {field.options.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </select>
            </div>
        );
    }
    return (
        <div className={cls}>
            <label className="form-label small text-muted mb-1">{field.label}</label>
            <CommitInput
                type={field.type || "text"}
                className="form-control"
                placeholder={field.placeholder}
                value={values[field.key] ?? ""}
                onChange={(v, now) => onChange(field.key, v, now)}
                onCommit={onCommit}
            />
        </div>
    );
};

/**
 * SUPER_ADMIN'e özel kolonların görünürlüğü. Tasarımda kırmızı işaretlenen
 * kolonlar (Debit/Credit oranı, Komisyon Kazancı, Kart Tipi) yalnız süper admine
 * gösterilir; kırmızı yalnızca tasarımda "admine özel" işaretiydi, stil değil.
 */
export const useIsSuperAdmin = () => {
    const { roles, loadingUser } = useProfileUser();
    return { isSuperAdmin: roles.includes("ROLE_SUPER_ADMIN"), loadingUser };
};

/**
 * Henüz toplanmayan veriler için hücre içeriği. Uydurma değer üretmek yerine
 * "—" gösterilir; tooltip sebebini açıklar.
 */
export const PendingCell = ({ hint = "Bu veri henüz toplanmıyor" }) => (
    <span className="text-muted" title={hint}>—</span>
);

/** Komisyon sağlayıcının işlem dökümünden değil Aktif POS oranından hesaplandıysa. */
export const EstimatedTag = ({ count }) => (
    <div
        className="text-muted"
        style={{ fontSize: 11 }}
        title={count != null
            ? `${count} işlemin komisyonu henüz PayTR'den gelmedi; Aktif POS oranıyla tahmin edildi`
            : "Komisyon henüz PayTR'den gelmedi; Aktif POS oranıyla tahmin edildi"}
    >
        tahmini
    </div>
);

/* --------------------------------- sıralama -------------------------------- */

/**
 * Metin olarak sıralanan kolonlar — backend'deki PosTableSorter listesiyle aynı
 * tutulmalıdır. Bu kolonlarda ilk tıklama A→Z, diğerlerinde çoktan aza sıralar.
 */
const TEXT_SORT_KEYS = new Set([
    "provider", "companyName", "accountName", "accountCode",
    "merchantOid", "cardBrand", "cardType",
]);

const DATE_SORT_KEYS = new Set(["lastTransactionDate", "paymentDate", "valorDate"]);

/** Kolona ilk tıklandığında uygulanan doğal yön. */
export const defaultSortDirection = (key) => (TEXT_SORT_KEYS.has(key) ? "asc" : "desc");

const TR_COLLATOR = new Intl.Collator("tr", { sensitivity: "base", numeric: true });

// "provider" kolonunda ekranda görünen ad sıralanır (backend de providerLabel'a bakar).
const sortValue = (row, key) =>
    key === "provider" ? row.providerLabel || providerLabelOf(row.provider) : row[key];

/**
 * Tamamı elde olan tablolarda (kök tablo, işlem dökümü) yerel sıralama. Sayfalı
 * listelerde sıralama backend'e bırakılır: istemcide yalnız görünen sayfa vardır,
 * burada sıralamak tüm kapsamı değil o 20 satırı sıralardı.
 *
 * Kurallar backend ile aynıdır: boş değerler yön ne olursa olsun sonda kalır,
 * metin kolonları Türkçe alfabeye göre ve büyük/küçük harf ayrımı olmadan sıralanır.
 */
export const sortPosRows = (rows, sort, direction) => {
    if (!sort || !rows?.length) return rows;
    const dir = direction === "asc" ? 1 : -1;
    const isText = TEXT_SORT_KEYS.has(sort);
    const isDate = DATE_SORT_KEYS.has(sort);
    const norm = (v) => {
        if (v == null || v === "") return null;
        if (isText) return String(v);
        if (isDate) {
            const t = Date.parse(v);
            return Number.isNaN(t) ? null : t;
        }
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
    };
    return [...rows].sort((a, b) => {
        const x = norm(sortValue(a, sort));
        const y = norm(sortValue(b, sort));
        if (x == null && y == null) return 0;
        if (x == null) return 1;
        if (y == null) return -1;
        if (isText) return TR_COLLATOR.compare(x, y) * dir;
        return (x < y ? -1 : x > y ? 1 : 0) * dir;
    });
};

/**
 * Kolon başlığı sıralamasının durumu: aynı kolona tekrar tıklamak yönü çevirir,
 * başka bir kolona geçmek o kolonun doğal yönüyle başlar.
 */
export const usePosSort = (defaultSort = null, defaultDirection = "desc") => {
    const [sort, setSort] = React.useState(defaultSort);
    const [direction, setDirection] = React.useState(defaultDirection);
    const toggle = React.useCallback((key) => {
        if (key === sort) {
            setDirection(direction === "asc" ? "desc" : "asc");
        } else {
            setSort(key);
            setDirection(defaultSortDirection(key));
        }
    }, [sort, direction]);
    return { sort, direction, toggle };
};

/** Tıklanabilir kolon başlığı. {@code sortKey} verilmezse düz başlık olarak çizilir. */
export const SortableTh = ({ sortKey, sort, direction, onSort, className = "", children }) => {
    // Sıralanamayan başlıklar da tek satırda kalmalı: "Firma Adı" iki satıra bölünüyordu.
    if (!sortKey || !onSort) {
        return <th className={`border-0 ${className}`} style={{ whiteSpace: "nowrap" }}>{children}</th>;
    }
    const active = sort === sortKey;
    const icon = active
        ? direction === "asc" ? "bx-up-arrow-alt" : "bx-down-arrow-alt"
        : "bx-sort-alt-2";
    return (
        <th
            className={`border-0 ${className}`}
            style={{
                cursor: "pointer",
                userSelect: "none",
                whiteSpace: "nowrap",
                color: active ? NAVY : undefined,
            }}
            onClick={() => onSort(sortKey)}
            title="Sıralamak için tıklayın"
        >
            {children}
            <i className={`bx ${icon} ms-1`} style={{ opacity: active ? 1 : 0.4, verticalAlign: "middle" }} />
        </th>
    );
};

/* ------------------------------- UI parçaları ------------------------------- */

const cardStyle = (accent) => ({
    borderRadius: 16,
    border: "1px solid #eef0f4",
    borderTop: accent ? `3px solid ${ORANGE}` : "1px solid #eef0f4",
    boxShadow: "0 2px 8px rgba(10,49,110,0.04)",
});

export const StatCard = ({ label, value, sub, accent }) => (
    <div className="bg-white h-100 p-3" style={cardStyle(accent)}>
        <div className="text-muted mb-1" style={{ fontSize: ".8rem" }}>{label}</div>
        <div className="fw-bold text-dark" style={{ fontSize: "1.35rem", lineHeight: 1.1 }}>{value}</div>
        {sub && <div className="text-muted mt-1" style={{ fontSize: ".72rem" }}>{sub}</div>}
    </div>
);

export const SectionTitle = ({ children }) => (
    <div className="d-flex align-items-center gap-3 mb-3 mt-2">
        <span
            className="fw-bold text-uppercase"
            style={{ color: "#8a93a6", fontSize: ".72rem", letterSpacing: ".06em" }}
        >
            {children}
        </span>
        <div style={{ flex: 1, height: 1, background: "#e7eaf0" }} />
    </div>
);

/** Hiyerarşi kırılımı. items: [{ label, to }] — son eleman aktif sayfadır. */
/**
 * Hiyerarşi kırılımı; {@code backTo} verilirse kırılımın solunda geri düğmesi
 * çizilir (panel genelindeki ortak {@link BackButton} stili).
 */
export const PosBreadcrumb = ({ items, backTo }) => (
    <nav className="d-flex align-items-center flex-wrap gap-2 mb-2" style={{ fontSize: ".82rem" }}>
        {backTo && (
            <BackButton to={backTo} />
        )}
        <span>
            {items.map((it, i) => {
                const last = i === items.length - 1;
                return (
                    <span key={i}>
                        {i > 0 && <span className="mx-2 text-muted">/</span>}
                        {last || !it.to ? (
                            <span className="fw-semibold" style={{ color: NAVY }}>{it.label}</span>
                        ) : (
                            <Link to={it.to} style={{ color: "#8a93a6", textDecoration: "none" }}>{it.label}</Link>
                        )}
                    </span>
                );
            })}
        </span>
    </nav>
);

/** Sayfa başlığı. Geri düğmesi kırılım satırındadır (bkz. PosBreadcrumb). */
export const PosPageHeader = ({ title, subtitle, right }) => (
    <div className="d-flex flex-wrap align-items-end justify-content-between gap-3 mb-3">
        <div>
            <h4 className="mb-1" style={{ color: NAVY }}>{title}</h4>
            {subtitle && <div className="text-muted" style={{ fontSize: ".85rem" }}>{subtitle}</div>}
        </div>
        <div className="d-flex align-items-center gap-2">{right}</div>
    </div>
);

/** Sayfalı listeler için basit sayfalama çubuğu. */
export const Pager = ({ page, totalPages, totalElements, onChange }) => {
    // Tek sayfaya sığan listede kayıt sayısı bilgi vermiyor; satırlar zaten görünüyor.
    if (!totalPages || totalPages <= 1) return null;
    return (
        <div className="d-flex align-items-center justify-content-between px-2 py-2">
            <span className="text-muted" style={{ fontSize: ".78rem" }}>
                Toplam {trNum(totalElements)} kayıt · Sayfa {page + 1}/{totalPages}
            </span>
            <div className="d-flex gap-1">
                <button className="btn btn-sm btn-light" disabled={page <= 0}
                        onClick={() => onChange(page - 1)}>
                    <i className="bx bx-chevron-left" />
                </button>
                <button className="btn btn-sm btn-light" disabled={page + 1 >= totalPages}
                        onClick={() => onChange(page + 1)}>
                    <i className="bx bx-chevron-right" />
                </button>
            </div>
        </div>
    );
};

export const TableCard = ({ children }) => (
    <div className="bg-white p-2 p-md-3" style={{ borderRadius: 16, border: "1px solid #eef0f4" }}>
        <div className="table-responsive">{children}</div>
    </div>
);

export const theadStyle = {
    fontSize: ".72rem",
    color: "#8a93a6",
    textTransform: "uppercase",
    letterSpacing: ".04em",
};


export const Loading = () => (
    <div className="text-center py-5"><Spinner /></div>
);

